/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { DataOptions } from "vuetify";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import _ from "lodash";
import { FILTER_NAMES } from "@/static/filterNames";
import {
  removeCostDocument,
  getCostDocumentDetails,
  getCostDocumentFiles,
  getCostDocumentItemsPairForCostDocument,
  getCostDocumentsTable,
  saveCostDocumentItemComment,
  approveCostDocument,
} from "@/requests/costDocument";
import { CostDocumentListItem } from "@/models/api/costDocuments/CostDocumentList";
import {
  CostDocumentItemsPair,
  CostDocumentItemsPairsResponse,
} from "@/models/api/costDocuments/CostDocumentItemsPairs";
import i18n from "@/plugins/i18n";
import { filterCapitalize } from "@/filters/capitalize";
import { CostDocumentDetails } from "@/models/api/costDocuments/CostDocumentDetails";
import costDocumentsAddEdit from "@/store/modules/costDocuments/costDocumentsAddEdit";
import { FileGuid } from "@/models/api/Files";
import CostDocumentsPairingModule from "@/store/modules/costDocuments/costDocumentsPairing";
import CostDocumentsAdvancedPairingModule from "@/store/modules/costDocuments/costDocumentsAdvancedPairing";
import { DoubleConfirm } from "@/models/api/DoubleConfirm";
import { assignCostDocument } from "@/requests/buffer";
import { COST_DOCUMENT_TYPES } from "@/static/costDocumentTypes";
import { ConfirmModel } from "@/models/components/popup/Confirm";

export interface CostDocumentsStateInterface {
  isCostDocumentsLoading: boolean;
  isCostDocumentItemsPairForCostDocumentLoading: boolean;
  costDocuments: Array<CostDocumentListItem>;
  costDocumentItemsPairForCostDocument: CostDocumentItemsPairsResponse;
  countCostDocuments: number;
  costDocumentsDataTableOptions: DataOptions;
  costDocumentsFilters: null | Array<CriterionBase>;
  selectedCostDocumentId: null | string;
  costDocumentDetailId: null | string;
  costDocumentDetails: null | CostDocumentDetails;
  costDocumentFiles: null | Array<FileGuid>;
  isExtendedInvoiceItems: boolean;
  costDocumentItemCommentId: null | number;
  costDocumentItemCommentText: null | string;
}

export const CostDocumentsState: CostDocumentsStateInterface = {
  isCostDocumentsLoading: false,
  isCostDocumentItemsPairForCostDocumentLoading: false,
  costDocuments: [],
  costDocumentItemsPairForCostDocument: [],
  countCostDocuments: 0,
  costDocumentsDataTableOptions: new DefaultDataOptions(),
  costDocumentsFilters: null,
  selectedCostDocumentId: null,
  costDocumentDetailId: null,
  costDocumentDetails: null,
  costDocumentFiles: null,
  isExtendedInvoiceItems: false,
  costDocumentItemCommentId: null,
  costDocumentItemCommentText: null,
};

const CostDocumentsModule = defineModule({
  state: CostDocumentsState as CostDocumentsStateInterface,
  modules: {
    costDocumentsAddEdit,
    pairing: CostDocumentsPairingModule,
    advancedPairing: CostDocumentsAdvancedPairingModule,
  },
  namespaced: true as const,
  getters: {},
  mutations: {
    setCostDocumentsDataTableOptions(state, param: DataOptions) {
      state.costDocumentsDataTableOptions = JSON.parse(JSON.stringify(param));
    },
    startCostDocumentsLoading(state) {
      state.isCostDocumentsLoading = true;
    },
    startCostDocumentItemsPairForCostDocumentLoading(state) {
      state.isCostDocumentItemsPairForCostDocumentLoading = true;
    },
    setCostDocumentsFilters(state, param: Array<CriterionBase>) {
      state.costDocumentsFilters = param;
    },
    setSelectedCostDocument(state, param: null | string) {
      state.selectedCostDocumentId = param;
    },
    setCostDocuments(state, param: { items: Array<CostDocumentListItem>; count: number }) {
      state.costDocuments = param.items;
      state.countCostDocuments = param.count;
      state.isCostDocumentsLoading = false;
    },
    setCostDocumentItemsPairForCostDocument(state, param: CostDocumentItemsPairsResponse) {
      state.costDocumentItemsPairForCostDocument = param;
      state.isCostDocumentItemsPairForCostDocumentLoading = false;
    },
    setCostDocumentDetailsId(state, param: string | null) {
      state.costDocumentDetailId = param;
    },
    setCostDocumentDetails(state, param: CostDocumentDetails | null) {
      state.costDocumentDetails = param;
    },
    setCostDocumentFiles(state, files: Array<FileGuid> | null) {
      state.costDocumentFiles = files;
    },
    setIsExtendedInvoiceItems(state, param: boolean) {
      state.isExtendedInvoiceItems = param;
    },
    setCostDocumentItemCommentId(state, param: null | number) {
      state.costDocumentItemCommentId = param;
    },
    setCostDocumentItemCommentText(state, param: null | string) {
      state.costDocumentItemCommentText = param;
    },
  },
  actions: {
    async getCostDocumentsTable(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.startCostDocumentsLoading();
      const response = await getCostDocumentsTable({
        count: state.costDocumentsDataTableOptions.itemsPerPage,
        page: state.costDocumentsDataTableOptions.page - 1,
        criterions: _.cloneDeep(state.costDocumentsFilters),
        filterName: FILTER_NAMES.CostDocumentListStandardFilter,
      });
      commit.setCostDocuments({
        items: response.items,
        count: response.totalCount,
      });
      if (response.items.length > 0) {
        commit.setSelectedCostDocument(response.items[0].id);
      }
    },
    async getCostDocumentItemsPairForCostDocument(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedCostDocumentId !== null) {
        commit.startCostDocumentItemsPairForCostDocumentLoading();
        const response = await getCostDocumentItemsPairForCostDocument(state.selectedCostDocumentId as string);
        commit.setCostDocumentItemsPairForCostDocument(response);
      }
    },
    async getCostDocumentFiles(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedCostDocumentId !== null) {
        commit.setCostDocumentFiles(null);
        const response = await getCostDocumentFiles(state.selectedCostDocumentId as string);
        commit.setCostDocumentFiles(response);
      }
    },
    async deleteCostDocument(context, costDocumentId) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      const confirmation = await rootDispatch.popup.addConfirm({
        title: filterCapitalize(i18n.t("warning").toString()),
        content: filterCapitalize(i18n.t("doYouReallyWantToDeleteCostDocument?").toString()),
        width: undefined,
        customButtons: null,
      });
      if (confirmation) {
        await rootDispatch.failIfRequestsInProgress();
        const result = await removeCostDocument(costDocumentId, false);

        await DoubleConfirm.Process(result, rootDispatch.popup.addConfirm, async () => {
          await removeCostDocument(costDocumentId, true);
        });
      }
      await dispatch.getCostDocumentsTable();
    },
    async getCostDocumentDetails(context, costDocumentId) {
      const { commit } = _moduleActionContext(context);
      commit.setCostDocumentDetails(null);
      commit.setCostDocumentDetailsId(costDocumentId);
      const details = await getCostDocumentDetails(costDocumentId);
      commit.setCostDocumentDetails(details);
    },
    async openCostDocumentItemCommentDialog(context, costDocumentItem: CostDocumentItemsPair) {
      const { commit } = _moduleActionContext(context);
      commit.setCostDocumentItemCommentId(costDocumentItem.id);
      commit.setCostDocumentItemCommentText(costDocumentItem.comment);
    },
    async saveCostDocumentItemComment(context) {
      const { state, commit, rootDispatch, dispatch } = _moduleActionContext(context);
      if (state.costDocumentItemCommentId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await saveCostDocumentItemComment(state.costDocumentItemCommentId, state.costDocumentItemCommentText);
        commit.setCostDocumentItemCommentId(null);
        rootDispatch.popup.addSavedOkSnackbar();
        await dispatch.getCostDocumentItemsPairForCostDocument();
      }
    },
    async returnCostDocumentToBuffer(context, costDocumentId: string) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);

      if (
        await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(i18n.t("areYouSureYouWantToReturnThisDocumentToBuffer")),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("no")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("yes")),
                color: "warning",
                value: true,
              },
            ],
            "450px"
          )
        )
      ) {
        await assignCostDocument(costDocumentId, COST_DOCUMENT_TYPES.Undefined);
        await dispatch.getCostDocumentsTable();
        await rootDispatch.popup.addSavedOkSnackbar();
      }
    },
    async approveCostDocument(context, costDocumentId) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      if (
        await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(i18n.t("areYouSureYouWantToApproveThisDocument")),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("no")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("yes")),
                color: "warning",
                value: true,
              },
            ],
            "450px"
          )
        )
      ) {
        await approveCostDocument(costDocumentId);
        await dispatch.getCostDocumentsTable();
        await rootDispatch.popup.addSavedOkSnackbar();
      }
    },
  },
});

export default CostDocumentsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, CostDocumentsModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, CostDocumentsModule);
